import { useCallback, useEffect, useMemo, useState } from 'react';
import type { VncEventDto, VncSessionDetailDto, VncSessionDto } from '@app/shared';
import { api, fetchImageUrl } from '../../api';
import { platform } from '../../platform';
import { useApp } from '../../state';
import { bytes, duration, Icon, Seg, Sheet, when } from '../../ui';
import { SubHead } from './SubHead';

const timeFmt = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

function eventText(e: VncEventDto): string {
  switch (e.kind) {
    case 'press':
      return `Нажатие (${e.x}, ${e.y})`;
    case 'drag':
      return `Протянул (${e.fromX}, ${e.fromY}) → (${e.x}, ${e.y})`;
    case 'key':
      return `Клавиша ${e.text}`;
    case 'paste':
      return `Вставка текста, ${e.text}`;
  }
}

type GroupBy = 'date' | 'panel' | 'employee';
type Period = 'today' | 'yesterday' | 'week' | 'month' | 'all';

const PERIODS: { value: Period; label: string }[] = [
  { value: 'today', label: 'Сегодня' },
  { value: 'yesterday', label: 'Вчера' },
  { value: 'week', label: '7 дней' },
  { value: 'month', label: '30 дней' },
  { value: 'all', label: 'Всё время' },
];

/** Границы периода в местном времени телефона. */
function periodRange(p: Period): { from?: string; to?: string } {
  const day = new Date();
  day.setHours(0, 0, 0, 0);
  const shift = (d: number) => new Date(day.getTime() + d * 86_400_000).toISOString();
  switch (p) {
    case 'today':
      return { from: shift(0) };
    case 'yesterday':
      return { from: shift(-1), to: shift(0) };
    case 'week':
      return { from: shift(-6) };
    case 'month':
      return { from: shift(-29) };
    case 'all':
      return {};
  }
}

const dayFmt = new Intl.DateTimeFormat('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' });
function dayTitle(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const y = new Date(Date.now() - 86_400_000);
  if (d.toDateString() === today.toDateString()) return 'Сегодня';
  if (d.toDateString() === y.toDateString()) return 'Вчера';
  const t = dayFmt.format(d);
  return t[0]!.toUpperCase() + t.slice(1);
}

function totalMinutes(list: VncSessionDto[]): number {
  return Math.round(list.reduce((a, s) => a + ((s.endedAt ? Date.parse(s.endedAt) : Date.now()) - Date.parse(s.startedAt)), 0) / 60_000);
}

function minutesText(m: number): string {
  return m < 60 ? `${m} мин` : `${Math.floor(m / 60)} ч ${m % 60} мин`;
}

/** Журнал подключений: кто сейчас на панелях, кто ходил раньше и что нажимал — по датам, панелям или людям. */
export function Sessions({ panelId }: { panelId?: string }) {
  const { fail, can, toast } = useApp();
  const [list, setList] = useState<VncSessionDto[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [groupBy, setGroupBy] = useState<GroupBy>('date');
  const [period, setPeriod] = useState<Period>('week');
  const [panel, setPanel] = useState<string>(panelId ?? '');
  const [person, setPerson] = useState<string>('');

  const load = useCallback(
    () =>
      api
        .sessions(periodRange(period))
        .then(setList)
        .catch(fail),
    [period, fail],
  );
  useEffect(() => {
    setList(null);
    void load();
    const t = setInterval(() => void load(), 10_000);
    return () => clearInterval(t);
  }, [load]);

  async function terminate(s: VncSessionDto) {
    if (!(await platform.confirm(`Отключить ${s.employeeName} от панели «${s.panelName}»?`))) return;
    try {
      await api.terminate(s.id);
      platform.haptic('success');
      toast('Сеанс завершён');
      await load();
    } catch (e) {
      fail(e);
    }
  }

  // Списки для выбора — из сеансов за выбранный период.
  const panels = useMemo(() => [...new Map((list ?? []).map((s) => [s.panelId, s.panelName])).entries()].sort((a, b) => a[1].localeCompare(b[1], 'ru')), [list]);
  const people = useMemo(() => [...new Map((list ?? []).map((s) => [s.employeeId, s.employeeName])).entries()].sort((a, b) => a[1].localeCompare(b[1], 'ru')), [list]);
  const shown = (list ?? []).filter((s) => (!panel || s.panelId === panel) && (!person || s.employeeId === person));
  const live = shown.filter((s) => s.live);
  const past = shown.filter((s) => !s.live);

  const groups = useMemo(() => {
    const m = new Map<string, { title: string; items: VncSessionDto[] }>();
    for (const s of past) {
      const key = groupBy === 'date' ? new Date(s.startedAt).toDateString() : groupBy === 'panel' ? s.panelId : s.employeeId;
      const title = groupBy === 'date' ? dayTitle(s.startedAt) : groupBy === 'panel' ? s.panelName : s.employeeName;
      const g = m.get(key) ?? { title, items: [] };
      g.items.push(s);
      m.set(key, g);
    }
    const arr = [...m.values()];
    if (groupBy !== 'date') arr.sort((a, b) => a.title.localeCompare(b.title, 'ru'));
    return arr;
  }, [past, groupBy]);

  const row = (s: VncSessionDto) => (
    <div key={s.id} className="item" style={{ alignItems: 'flex-start' }}>
      <button type="button" className="grow stack" style={{ gap: 3, background: 'none', border: 'none', padding: 0, textAlign: 'left', color: 'inherit' }} onClick={() => setOpenId(s.id)}>
        <span className="row" style={{ gap: 8 }}>
          <span className="title ellipsis grow">{groupBy === 'panel' ? s.employeeName : s.panelName}</span>
          {s.mode === 'view' && <span className="badge outline">просмотр</span>}
        </span>
        <span className="sub">
          {groupBy === 'panel' ? '' : groupBy === 'employee' ? '' : `${s.employeeName} · `}
          {groupBy === 'employee' && `${s.panelName} · `}
          {groupBy === 'date' ? timeFmt.format(new Date(s.startedAt)).slice(0, 5) : when(s.startedAt)} ·{' '}
          {s.live ? `идёт ${duration(s.startedAt, null)}` : duration(s.startedAt, s.endedAt)}
        </span>
        <span className="sub">
          нажатий {s.clicks} · клавиш {s.keys} · снимков {s.shots}
          {s.bytesDown > 0 ? ` · ${bytes(s.bytesDown)}` : ''}
        </span>
        {s.endReason && <span className="sub">{s.endReason}</span>}
      </button>
      {s.live && can('session.terminate') && (
        <button type="button" className="btn btn-danger btn-sm" onClick={() => void terminate(s)}>
          Отключить
        </button>
      )}
    </div>
  );

  return (
    <div className="stack">
      <SubHead title="Журнал" />

      <Seg<GroupBy>
        label="Группировать"
        value={groupBy}
        onChange={setGroupBy}
        options={[
          { value: 'date', label: 'По датам' },
          { value: 'panel', label: 'По панелям' },
          { value: 'employee', label: 'По людям' },
        ]}
      />
      <div className="chips" role="group" aria-label="Период">
        {PERIODS.map((p) => (
          <button key={p.value} type="button" className="chip" aria-pressed={period === p.value} onClick={() => setPeriod(p.value)}>
            {p.label}
          </button>
        ))}
      </div>
      <div className="grid2" style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)' }}>
        <select className="input" aria-label="Панель" value={panel} onChange={(e) => setPanel(e.target.value)}>
          <option value="">Все панели</option>
          {panels.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </select>
        <select className="input" aria-label="Сотрудник" value={person} onChange={(e) => setPerson(e.target.value)}>
          <option value="">Все сотрудники</option>
          {people.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </select>
      </div>

      {list && (
        <div className="small muted">
          Сеансов: {shown.length} · общее время {minutesText(totalMinutes(shown))} · нажатий {shown.reduce((a, s) => a + s.clicks, 0)}
        </div>
      )}

      {!list && <div className="skeleton" />}
      {live.length > 0 && (
        <>
          <h2 className="section-title">Сейчас на панелях · {live.length}</h2>
          <div className="list">{live.map(row)}</div>
        </>
      )}
      {list && shown.length === 0 && <p className="muted">За этот период подключений нет.</p>}
      {groups.map((g) => (
        <section key={g.title} className="stack" style={{ gap: 8 }}>
          <h2 className="section-title">
            {g.title} · {g.items.length} · {minutesText(totalMinutes(g.items))} · нажатий {g.items.reduce((a, s) => a + s.clicks, 0)}
          </h2>
          <div className="list">{g.items.map(row)}</div>
        </section>
      ))}
      {openId && <SessionSheet id={openId} onClose={() => setOpenId(null)} onTerminate={(s) => void terminate(s).then(() => setOpenId(null))} />}
    </div>
  );
}

function Shot({ sessionId, shotId }: { sessionId: string; shotId: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [big, setBig] = useState(false);
  useEffect(() => {
    let url: string | null = null;
    fetchImageUrl(`/sessions/${sessionId}/shots/${shotId}`)
      .then((u) => setSrc((url = u)))
      .catch(() => {});
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [sessionId, shotId]);
  if (!src) return <div className="skeleton" style={{ minHeight: 90 }} />;
  return (
    <button type="button" onClick={() => setBig((b) => !b)} style={{ padding: 0, border: '1px solid var(--line)', borderRadius: 10, overflow: 'hidden', background: '#111', width: big ? '100%' : 160 }} aria-label="Снимок экрана — увеличить">
      <img src={src} alt="Экран панели в момент нажатия" style={{ display: 'block', width: '100%' }} />
    </button>
  );
}

function SessionSheet({ id, onClose, onTerminate }: { id: string; onClose: () => void; onTerminate: (s: VncSessionDto) => void }) {
  const { fail, can } = useApp();
  const [d, setD] = useState<VncSessionDetailDto | null>(null);
  useEffect(() => {
    api.sessionDetail(id).then(setD).catch(fail);
  }, [id, fail]);

  // Действия и снимки — одной лентой по времени.
  const items = d
    ? [...d.events.map((e) => ({ at: e.at, ev: e, shot: null as string | null })), ...d.shotList.map((s) => ({ at: s.at, ev: null as VncEventDto | null, shot: s.id }))].sort((a, b) =>
        a.at.localeCompare(b.at),
      )
    : [];

  return (
    <Sheet title={d ? d.panelName : 'Сеанс'} onClose={onClose}>
      {!d ? (
        <div className="skeleton" />
      ) : (
        <>
          <div className="small">
            <b>{d.employeeName}</b> · {when(d.startedAt)} · {d.live ? `идёт ${duration(d.startedAt, null)}` : duration(d.startedAt, d.endedAt)}
            {d.screen ? ` · экран ${d.screen.width}×${d.screen.height}` : ''}
            {d.clientIp ? ` · IP ${d.clientIp}` : ''}
          </div>
          {d.endReason && <div className="small muted">{d.endReason}</div>}
          {d.live && can('session.terminate') && (
            <button type="button" className="btn btn-danger btn-block" onClick={() => onTerminate(d)}>
              <Icon name="close" size={18} />
              Отключить сейчас
            </button>
          )}
          <h3 className="section-title">Действия · {d.events.length}</h3>
          {items.length === 0 && <p className="muted small">Ничего не нажимали.</p>}
          <div className="stack" style={{ gap: 6 }}>
            {items.map((it, i) => (
              <div key={i} className="row" style={{ alignItems: 'flex-start', gap: 10 }}>
                <span className="small muted" style={{ width: 62, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                  {timeFmt.format(new Date(it.at))}
                </span>
                {it.shot ? <Shot sessionId={d.id} shotId={it.shot} /> : <span className="small">{eventText(it.ev!)}</span>}
              </div>
            ))}
          </div>
        </>
      )}
    </Sheet>
  );
}
